import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { IFieldConfig } from '../../../../../interface/field-config.interface';
import { FieldTextStateless } from '@atlaskit/field-text';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../../../interface/component/injected-props.interface';
import styled from 'styled-components';
import {BlockColorEnum, BlockColorType} from '../../../../../enum/block-color.enum';
import {action, observable, runInAction} from 'mobx';
import {getSelectedCountry} from '../../../../../util/phone-number/get-selected-country.util';
import {getGooglePhoneLib} from '../../../../../util/phone-number/get-google-phone-lib';
import {checkIsDigitsPrefixedWithPlus} from '../../../../../util/phone-number/check-is-digits-prefixed-plus.util';
import {formatPhoneNumberAsString} from '../../../../../util/phone-number/format-phone-string.util';
import countries, {countryNone, ICountry} from '../../../../../util/phone-number/countries';
import {countNonNumericChars} from '../../../../../util/phone-number/count-non-numeric-chars.util';
import {formatPhoneNumber} from '../../../../../util/phone-number/format-phone-number.util';
import {PhoneNumberCountryPicker} from './phone-number-country-picker';
import Loader from '../../../../loaders/loader';

interface IPhoneFieldProps {
  onChange: any;
  onBlur: any;
  value: string;
  isValid?: boolean;
  invalidityMessage?: React.ReactNode;
  labelColor: string;
  preferredCountries: string[];
  defaultCountryOnNew?: string;
  showAllCountriesLabel: string;
  hideAllCountriesLabel: string;
  closeCountryPickerLabel: string;
  searchIconColor: BlockColorType;
  buttonColor: BlockColorType;
}

const DROP_DOWN_BUTTON_WDITH = 95;

const FieldComponentWrapper = styled.div`
  position: relative;
  align-items: flex-end;
  & label div {
    color: ${(props: { labelColor: string }) => props.labelColor} !important;
  }
  & input {
    height: 36px;
    margin-left:${DROP_DOWN_BUTTON_WDITH + 5}px;
    background: ${BlockColorEnum.SecondaryTextColor};
  }
  & div div div {
    background: ${BlockColorEnum.SecondaryTextColor};
  }
  & div div div:hover {
    background: ${BlockColorEnum.SecondaryTextColor};
  }
  & textarea {
    height: 132px;
    background: ${BlockColorEnum.SecondaryTextColor};
  }
`;

const FieldCharacterCount = styled.div`
  position: absolute;
  bottom: -20px;
  right: 0;
  pointer-events: none;
  opacity: ${(props: {isVisible: boolean}) => props.isVisible ? 1 : 0};
  transition: opacity 500ms;
  display: flex;
  flex-direction: row-reverse;
`;

const CountContainer = styled.span`
  color: ${(props: {isExceeded: boolean}) => props.isExceeded ? BlockColorEnum.ErrorColor : BlockColorEnum.InactiveColor};
`;

const VALID_BORDER_COLOR: string = '#DFE1E6';
const INVALID_BORDER_COLOR: string = '#DE350B';
const HOVER_BORDER_COLOR: string = '#EBECF0';

const DropdownButton = styled.div`
  bottom: 2px;
  left: 2px;
  padding: 0 10px;
  border-right: 1px solid #F7FAFC;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
  min-width: ${DROP_DOWN_BUTTON_WDITH}px;
  width: ${DROP_DOWN_BUTTON_WDITH}px;
  height: 48px;
  max-width: ${DROP_DOWN_BUTTON_WDITH}px;
  max-height: 48px;
  border-right: 2px solid ${(props: {focused: boolean, isInvalid: boolean}) => props.focused ? (props as any).theme['@atlaskit-shared-theme/item'].focus.outline : (props.isInvalid ? INVALID_BORDER_COLOR : VALID_BORDER_COLOR)};
  :hover {
    background-color: #F7FAFC;
    border-color: ${(props: {focused: boolean, isInvalid: boolean}) => props.focused ? (props as any).theme['@atlaskit-shared-theme/item'].focus.outline : (props.isInvalid ? INVALID_BORDER_COLOR : HOVER_BORDER_COLOR) };
  }
`;

const LoaderHolder = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
`;

@inject('state')
@observer
export class PhoneField extends React.Component<IFieldConfig & IPhoneFieldProps & IInjectedProps> {
  @observable focused: boolean = false;
  @observable.shallow libphonenumber: typeof libphonenumber;
  countryPreselectedByUser: boolean = false;
  @observable selectedCountry: ICountry = countryNone;
  @observable countryPickerOpen: boolean = false;
  textFieldRef: any;
  previousValue: string;
  id: string;
  mouseY: number;

  @action
  onFocus = () => this.focused = true

  @action
  onBlur = () => {
    this.focused = false;
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  }

  get inputNode() {
    return ReactDOM.findDOMNode(this.textFieldRef).childNodes[1].childNodes[0].childNodes[0].childNodes[0];
  }

  async componentDidMount() {
    const libphonenumber = await getGooglePhoneLib();
    runInAction(() => {
      this.libphonenumber = libphonenumber;
    });
  }

  getPhoneNumberUtil = () => {
    return this.libphonenumber.PhoneNumberUtil.getInstance();
  }

  @action
  onChange = (event) => {
    const value = event.target.value;
    if (!this.libphonenumber) {
      return;
    }
    if (!checkIsDigitsPrefixedWithPlus(value)) {
      return;
    }
    if (!value) {
      if (!this.countryPreselectedByUser) {
        this.selectedCountry = getSelectedCountry(this.getPhoneNumberUtil(), value, this.props.defaultCountryOnNew);
      }
      this.props.onChange('');
      return;
    }
    let phoneNumberValue = value || '';
    if (this.selectedCountry && !phoneNumberValue.startsWith('+')) {
      phoneNumberValue = this.selectedCountry.callingCode + value;
    }
    phoneNumberValue = phoneNumberValue.replace(' ', '');
    const selectedCountryFromPhone = getSelectedCountry(this.getPhoneNumberUtil(), phoneNumberValue);
    if (selectedCountryFromPhone.countryCode && this.selectedCountry.countryCode !== selectedCountryFromPhone.countryCode) {
      this.selectedCountry = selectedCountryFromPhone;
    }

    const formattedPhone = formatPhoneNumberAsString(this.getPhoneNumberUtil(), phoneNumberValue);
    this.props.onChange(formattedPhone);

    // Correct the cursor position after formatting
    const cursorStartBefore = this.inputNode.selectionStart;
    const cursorEndBefore = this.inputNode.selectionEnd;
    const blanksBefore = countNonNumericChars(this.previousValue, cursorStartBefore);
    setTimeout(() => {
      const valueAfter = this.inputNode.value;
      const blanksAfter = countNonNumericChars(valueAfter, cursorStartBefore, !blanksBefore);
      const cursorStart = cursorStartBefore + blanksAfter - blanksBefore;
      const cursorEnd = cursorEndBefore + blanksAfter - blanksBefore;
      this.inputNode.setSelectionRange(cursorStart, cursorEnd);
    }, 10);
  }

  onKeyDown = (event) => {
    this.previousValue = event.target.value;
  }

  @action
  openCountryEdit = (event) => {
    this.mouseY = event.clientY;
    this.countryPickerOpen = true;
    document.body.style.overflow = 'hidden';
  }

  @action
  closeCountryEdit = () => {
    this.countryPickerOpen = false;
    document.body.style.overflow = 'auto';
  }

  getValueWithNewCountryCode = () => {
    const selectedCountryFromPhone = getSelectedCountry(this.getPhoneNumberUtil(), this.props.value, this.props.defaultCountryOnNew);
    if (this.props.value && selectedCountryFromPhone.countryCode && this.selectedCountry.countryCode !== selectedCountryFromPhone.countryCode) {
      return this.props.value.replace(selectedCountryFromPhone.callingCode, this.selectedCountry.callingCode);
    }
    return this.props.value;
  }

  @action
  onCountrySelected = (c: ICountry) => {
    this.selectedCountry = c;
    this.closeCountryEdit();
    this.countryPreselectedByUser = true;
    this.props.onChange(this.getValueWithNewCountryCode());
  }

  @action
  componentWillMount() {
    this.id = `${this.props.applyFormKey}`;
    if (this.props.defaultCountryOnNew) {
      const defaultOnNew = countries.find(x => x.countryCode.toLowerCase() === this.props.defaultCountryOnNew.toLowerCase());
      if (defaultOnNew) {
        this.selectedCountry = defaultOnNew;
      }
    }
  }

  render() {
    if (!this.libphonenumber) {
      return <LoaderHolder><Loader height={55} width={100} style={{margin: '40px auto 0 auto'}}/></LoaderHolder>;
    }
    const CountryIcon = this.selectedCountry.icon;
    const props = this.props;
    const maxLength = props.maxChars !== 0 ? props.maxChars : undefined;
    let phoneNumberValue = props.value || '';
    if (this.selectedCountry && !phoneNumberValue.startsWith('+')) {
      phoneNumberValue = this.selectedCountry.callingCode + phoneNumberValue;
    }
    const formattedValue = formatPhoneNumber(this.getPhoneNumberUtil(), this.libphonenumber.PhoneNumberFormat.INTERNATIONAL, phoneNumberValue, true, this.selectedCountry && this.selectedCountry.callingCode);
    let formattedPhoneNumber = formattedValue.phoneNumber;
    if (formattedPhoneNumber && this.selectedCountry.callingCode && formattedPhoneNumber.startsWith(this.selectedCountry.callingCode)) {
      formattedPhoneNumber = formattedPhoneNumber.replace(this.selectedCountry.callingCode, '');
    }
    return (
        <FieldComponentWrapper labelColor={props.labelColor}>
          <PhoneNumberCountryPicker
              isOpen={this.countryPickerOpen}
              onClose={this.closeCountryEdit}
              width={300}
              opacity={1}
              buttonColor={this.props.buttonColor}
              onCountrySelected={this.onCountrySelected}
              hideAllCountriesLabel={this.props.hideAllCountriesLabel}
              showAllCountriesLabel={this.props.showAllCountriesLabel}
              closeCountryPickerLabel={this.props.closeCountryPickerLabel}
              preferredCountries={this.props.preferredCountries}
              searchPlaceholder="Search"
              searchIconColor={this.props.searchIconColor}
              triggerStyle={{position: 'absolute', left: 2, bottom: 2, width: DROP_DOWN_BUTTON_WDITH, height: 48}}
              trigger={<DropdownButton isInvalid={!props.isValid} focused={this.focused} onClick={this.openCountryEdit}>
                <CountryIcon style={{marginRight: 5, verticalAlign: 'middle'}} size={15} />
                <div>
                  {this.selectedCountry.callingCode}
                </div>
              </DropdownButton>}
          />
          <FieldTextStateless
              id={this.id}
              disabled={props.disabled}
              label={props.label}
              placeholder={props.placeholder}
              name={props.applyFormKey}
              shouldFitContainer={true}
              required={props.required}
              isInvalid={!props.isValid}
              value={formattedPhoneNumber}
              invalidMessage={props.invalidityMessage}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              ref={(element) => {
                this.textFieldRef = element;
              }}
              onKeyDown={this.onKeyDown}
              onChange={this.onChange}
          />
          <FieldCharacterCount isVisible={this.focused && Boolean(maxLength) && Boolean(props.value)}>
            <CountContainer isExceeded={false}>&nbsp;{`/${maxLength}`}</CountContainer>
            <CountContainer isExceeded={maxLength && Boolean(props.value) && props.value.length > maxLength}>{Boolean(props.value) ? props.value.length : '0'}</CountContainer>
          </FieldCharacterCount>
        </FieldComponentWrapper>
    );
  }
}
